import config from '@/config';
import type { App } from 'vue';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

interface ConsentSettings {
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
}

interface FacebookEventData {
  user_email: string;
  user_id: string;
  value?: number;
  currency?: string;
}

interface PurchaseEventData {
  facebook_pixel_id: string;
  facebook_event_name: string;
  facebook_event_id: string;
  facebook_event_data: FacebookEventData;
  currency: string;
  value: number;
  user_data: {
    email_address: string;
  };
  user_id: string;
  ecommerce: {
    transaction_id: string;
    value: number;
    currency: string;
    purchase: {
      actionField: {
        id: string;
        revenue: number;
      };
      products: Array<{
        name: string;
        id: number;
        price: number;
        quantity: number;
      }>;
    };
    user: {
      id: string;
    };
  };
}

export class Analytics {
  constructor(app: App) {}

  init(cookiesAllowed: boolean): void {
    this.setConsent(cookiesAllowed);

    // Google Tag Manager
    (function (w: Window, d: Document, s: string, l: string, i: string) {
      w[l] = w[l] || [];
      w[l].push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      });
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s) as HTMLScriptElement;
      const dl = l !== 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
      f.parentNode?.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', config.google.gtm);
  }

  setConsent(cookiesAllowed: boolean): void {
    window.dataLayer = window.dataLayer || [];
    const settings: ConsentSettings = cookiesAllowed
      ? {
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
          analytics_storage: 'granted',
        }
      : {
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          analytics_storage: 'denied',
        };

    window.dataLayer.push(['consent', 'default', settings]);
  }

  updateConsent(): void {
    window.dataLayer = window.dataLayer || [];
    const settings: ConsentSettings = {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
    };

    window.dataLayer.push(['consent', 'update', settings]);
  }

  sendEvent(eventName: string, eventData: Record<string, any>): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ...eventData, event: eventName });
  }

  sendPurchaseEvent(userEmail: string, userId: string, transactionId: string, amount: number, currency = 'USD'): void {
    const eventData: PurchaseEventData = {
      facebook_pixel_id: config.facebook.pixel,
      facebook_event_name: 'Purchase',
      facebook_event_id: `PRC_${transactionId}`,
      facebook_event_data: {
        value: amount,
        currency,
        user_email: userEmail,
        user_id: userId,
      },
      currency,
      value: amount,
      user_data: {
        email_address: userEmail,
      },
      user_id: userId,
      ecommerce: {
        transaction_id: transactionId,
        value: amount,
        currency,
        purchase: {
          actionField: {
            id: transactionId,
            revenue: amount,
          },
          products: [
            {
              name: `Add funds ${amount}`,
              id: amount,
              price: amount,
              quantity: 1,
            },
          ],
        },
        user: {
          id: userId,
        },
      },
    };

    this.sendEvent('PurchaseCompleted', eventData);
  }

  sendStartChatEvent(userEmail: string, userId: string, advisorUrl: string): void {
    const date = new Date();
    const eventId = `SC_${advisorUrl}_${date.getFullYear()}${String(date.getMonth()).padStart(2, '0')}${String(date.getDate()).padStart(
      2,
      '0'
    )}${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}`;

    this.sendEvent('StartChat', {
      facebook_pixel_id: config.facebook.pixel,
      facebook_event_name: 'Contact',
      facebook_event_id: eventId,
      facebook_event_data: {
        user_email: userEmail,
        user_id: userId,
      },
      user_data: {
        email_address: userEmail,
      },
      user_id: userId,
    });
  }

  sendPaymentFailedEvent(userEmail: string, userId: string): void {
    const date = new Date();
    const eventId = `PF_${userId}_${date.getFullYear()}${String(date.getMonth()).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}${String(
      date.getHours()
    ).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}`;

    this.sendEvent('PaymentFailed', {
      facebook_pixel_id: config.facebook.pixel,
      facebook_event_name: 'AddToWishlist',
      facebook_event_id: eventId,
      facebook_event_data: {
        user_email: userEmail,
        user_id: userId,
      },
      user_data: {
        email_address: userEmail,
      },
      user_id: userId,
    });
  }

  sendCompleteRegistrationEvent(userEmail: string, userId: string): void {
    this.sendEvent('RegistrationCompleted', {
      facebook_pixel_id: config.facebook.pixel,
      facebook_event_name: 'CompleteRegistration',
      facebook_event_id: `CRG_${userId}`,
      facebook_event_data: {
        user_email: userEmail,
        user_id: userId,
      },
      user_data: {
        email_address: userEmail,
      },
      user_id: userId,
    });
  }
}

export default {
  install: (app: App) => {
    const analytics = new Analytics(app);
    app.config.globalProperties.analytics = analytics;
    app.config.globalProperties.$analytics = analytics;
    // @ts-ignore
    app.$analytics = analytics;
  },
};
